import {
  FUEL_COST_MOVEMENT_KIND_LABELS,
  FUEL_COST_SOURCE_TYPE_LABELS,
  FUEL_LEDGER_STATUS_LABELS,
  type FuelDailyClosing,
  type FuelStockCost,
} from "@station/contracts";
import { addDays, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  type DataTableColumn,
  DateInput,
  Drawer,
  EmptyState,
  FormField,
  QuantityText,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@station/ui";
import { Money } from "@station/core";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CalendarCheck, Coins, PackagePlus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { newIdempotencyKey } from "@station/api-client";
import { Field, Section } from "../../../features/expenses/shared.js";
import { movementDirection, unitCostDisplay } from "../../../features/fuel/cost-values.js";
import { OpeningStockDialog } from "../../../features/fuel/opening-stock-dialog.js";
import {
  useFuelCostHistory,
  useFuelDailyClosings,
  useFuelPermissions,
  useFuelStockCosts,
  useRefreshFuel,
} from "../../../features/fuel/queries.js";
import {
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  Th,
  useFuelStation,
} from "../../../features/fuel/shared.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../lib/format.js";
import { useStepUp } from "../../../lib/step-up.js";

const TABS = ["costs", "closings"] as const;

const searchSchema = z.object({
  ...fuelSearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
  cost: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/akaryakit/maliyet")({
  validateSearch: (search) => searchSchema.parse(search),
  component: FuelCostPage,
});

/**
 * Stok maliyeti ve gün kapanışı. Maliyet HAREKETLİ AĞIRLIKLI ORTALAMADIR ve kapsamı ayardır
 * (istasyon × ürün ya da şirket × ürün): şirket kapsamında satırın istasyonu boştur.
 */
function FuelCostPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useFuelPermissions();
  const tab = search.tab ?? "costs";

  return (
    <FuelPage
      title={t("menu.fuelCost")}
      description={t("fuel.cost.description")}
      breadcrumb={[{ label: t("menu.fuelCost") }]}
    >
      {({ companyId }) =>
        !permissions.canRead ? (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        ) : (
          <Tabs
            value={tab}
            onValueChange={(next) =>
              void navigate({
                to: ".",
                search: ((previous: Record<string, unknown>) => ({ ...previous, tab: next })) as never,
                replace: true,
              })
            }
          >
            <TabsList aria-label={t("fuel.cost.tabs")}>
              {TABS.map((value) => (
                <TabsTrigger key={value} value={value}>
                  {t(`fuel.cost.tab.${value}`)}
                </TabsTrigger>
              ))}
            </TabsList>
            {/* Her sekmenin İÇERİĞİ mutlaka `TabsContent` içindedir (aksi hâlde tab paneli erişilemez olur). */}
            <TabsContent value="costs">
              {tab === "costs" ? <StockCostsTab companyId={companyId} /> : null}
            </TabsContent>
            <TabsContent value="closings">
              {tab === "closings" ? <DailyClosingsTab companyId={companyId} /> : null}
            </TabsContent>
          </Tabs>
        )
      }
    </FuelPage>
  );
}

function StockCostsTab({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useFuelStation(companyId);
  const costs = useFuelStockCosts(companyId, station.stationId);
  const permissions = useFuelPermissions();
  const [openingOpen, setOpeningOpen] = useState(false);
  const rows = costs.data?.items ?? [];
  const selected = rows.find((row) => row.id === search.cost) ?? null;

  const columns = useMemo<DataTableColumn<FuelStockCost>[]>(
    () => [
      {
        id: "scope",
        header: t("fuel.cost.fields.scope"),
        hideable: false,
        exportValue: (row) => (row.stationId === null ? "company" : "station"),
        cell: (row) => (row.stationId === null ? t("fuel.cost.companyScope") : t("fuel.cost.stationScope")),
      },
      {
        id: "product",
        header: t("fuel.cost.fields.product"),
        exportValue: (row) => row.productName,
        cell: (row) => row.productName,
      },
      {
        id: "quantityL",
        header: t("fuel.cost.fields.quantity"),
        align: "right",
        exportValue: (row) => row.quantityL,
        cell: (row) => <QuantityText value={row.quantityL} unit="LITRE" />,
      },
      {
        id: "averageUnitCost",
        header: t("fuel.cost.fields.averageUnitCost"),
        align: "right",
        exportValue: (row) => row.averageUnitCost,
        cell: (row) => <UnitCost value={row.averageUnitCost} />,
      },
      {
        id: "stockValue",
        header: t("fuel.cost.fields.stockValue"),
        align: "right",
        exportValue: (row) => row.stockValue,
        cell: (row) => Money.fromMinor(row.stockValue).formatTr(),
      },
      {
        id: "updatedAt",
        header: t("fuel.cost.fields.updatedAt"),
        exportValue: (row) => row.updatedAt,
        cell: (row) => formatDateTime(row.updatedAt),
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <div className="grid gap-4">
      <DataTable
        caption={t("fuel.cost.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={costs.isPending}
        error={costs.error ? <Alert tone="danger">{errorMessage(costs.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Coins />}
            title={t("fuel.cost.emptyTitle")}
            description={t("fuel.cost.emptyDescription")}
          />
        }
        onRowClick={(row) =>
          void navigate({
            to: ".",
            search: ((previous: Record<string, unknown>) => ({ ...previous, cost: row.id })) as never,
            replace: true,
          })
        }
        filters={<StationField selection={station} />}
        toolbar={
          permissions.canManageSettings ? (
            <Button variant="outline" onClick={() => setOpeningOpen(true)}>
              <PackagePlus aria-hidden="true" />
              {t("fuel.opening.open")}
            </Button>
          ) : undefined
        }
      />
      <p className="text-xs text-muted-foreground">{t("fuel.cost.rowHint")}</p>

      <OpeningStockDialog
        stations={station.stations}
        initialStationId={station.stationId}
        open={openingOpen}
        onOpenChange={setOpeningOpen}
      />

      <CostHistoryDrawer
        item={selected}
        onClose={() =>
          void navigate({
            to: ".",
            search: ((previous: Record<string, unknown>) => {
              const { cost: _cost, ...rest } = previous;
              return rest;
            }) as never,
            replace: true,
          })
        }
      />
    </div>
  );
}

/** Birim maliyet 6 ondalıkla tutulur; ekranda 4 ondalık okunur, tam değer `title`'dadır. */
function UnitCost({ value }: { value: string | null }): ReactElement {
  const display = unitCostDisplay(value);
  if (display === null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="tabular whitespace-nowrap" {...(display.truncated ? { title: display.fullText } : {})}>
      {display.text}
    </span>
  );
}

function CostHistoryDrawer({
  item,
  onClose,
}: {
  item: FuelStockCost | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const pages = useCursorPages(`${item?.id ?? "none"}:${includeSuperseded}`);
  const history = useFuelCostHistory(item?.id ?? null, includeSuperseded, pages.cursor);
  const entries = history.data?.items ?? [];

  return (
    <Drawer
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={
        item ? t("fuel.cost.history.title", { product: item.productName }) : t("fuel.cost.history.empty")
      }
    >
      {item === null ? null : (
        <div className="grid gap-4">
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Field label={t("fuel.cost.fields.quantity")}>
              <QuantityText value={item.quantityL} unit="LITRE" />
            </Field>
            <Field label={t("fuel.cost.fields.averageUnitCost")}>
              <UnitCost value={item.averageUnitCost} />
            </Field>
            <Field label={t("fuel.cost.fields.stockValue")}>
              {Money.fromMinor(item.stockValue).formatTr()}
            </Field>
            <Field label={t("fuel.cost.fields.updatedAt")}>{formatDateTime(item.updatedAt)}</Field>
          </dl>

          <label className="flex min-h-9 w-fit items-center gap-2 text-sm">
            <Checkbox
              checked={includeSuperseded}
              onChange={(event) => setIncludeSuperseded(event.target.checked)}
            />
            {t("fuel.cost.history.includeSuperseded")}
          </label>
          <p className="text-xs text-muted-foreground">{t("fuel.cost.history.supersededHint")}</p>

          {history.error ? (
            <Alert tone="danger">{errorMessage(history.error)}</Alert>
          ) : !history.data ? (
            <SkeletonRows rows={4} />
          ) : entries.length === 0 ? (
            <EmptyState headingLevel={3} title={t("fuel.cost.history.emptyRows")} />
          ) : (
            <div
              className="overflow-x-auto rounded-lg border"
              tabIndex={0}
              role="group"
              aria-label={t("fuel.cost.history.tableLabel")}
            >
              <table className="w-full text-sm">
                <caption className="sr-only">{t("fuel.cost.history.tableLabel")}</caption>
                <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                  <tr>
                    <Th>{t("fuel.cost.history.fields.effectiveAt")}</Th>
                    <Th>{t("fuel.cost.history.fields.kind")}</Th>
                    <Th>{t("fuel.cost.history.fields.source")}</Th>
                    <Th align="right">{t("fuel.cost.history.fields.quantity")}</Th>
                    <Th align="right">{t("fuel.cost.history.fields.unitCost")}</Th>
                    <Th align="right">{t("fuel.cost.history.fields.amount")}</Th>
                    <Th align="right">{t("fuel.cost.history.fields.quantityAfter")}</Th>
                    <Th align="right">{t("fuel.cost.history.fields.averageAfter")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className={`border-t ${entry.superseded ? "opacity-60" : ""}`}>
                      <td className="px-3 py-2">
                        {formatDateTime(entry.effectiveAt)}
                        {entry.superseded ? (
                          <>
                            {" "}
                            <StatusBadge tone="neutral">{t("fuel.cost.history.superseded")}</StatusBadge>
                          </>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">{FUEL_COST_MOVEMENT_KIND_LABELS[entry.kind]}</td>
                      <td className="px-3 py-2">{FUEL_COST_SOURCE_TYPE_LABELS[entry.sourceType]}</td>
                      <td className="px-3 py-2 text-right">
                        <span className="sr-only">
                          {t(`fuel.cost.history.direction.${movementDirection(entry.quantityL)}`)}
                        </span>
                        <QuantityText value={entry.quantityL} unit="LITRE" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <UnitCost value={entry.unitCost} />
                      </td>
                      <td className="px-3 py-2 text-right">{Money.fromMinor(entry.amount).formatTr()}</td>
                      <td className="px-3 py-2 text-right">
                        <QuantityText value={entry.quantityAfterL} unit="LITRE" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <UnitCost value={entry.averageAfter} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <nav className="flex justify-end gap-2" aria-label={t("fuel.cost.history.pagination")}>
            <Button
              variant="outline"
              size="sm"
              disabled={!pages.hasPrevious}
              onClick={() => pages.previous()}
            >
              {t("fuel.cost.history.previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!(history.data?.page.hasMore ?? false)}
              onClick={() => pages.next(history.data?.page.nextCursor)}
            >
              {t("fuel.cost.history.next")}
            </Button>
          </nav>
        </div>
      )}
    </Drawer>
  );
}

function DailyClosingsTab({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const station = useFuelStation(companyId);
  const range = useMemo(() => {
    const today = toIstanbulDate(new Date());
    return { from: addDays(today, -30), to: today };
  }, []);
  const pages = useCursorPages(`${station.stationId ?? "none"}`);
  const closings = useFuelDailyClosings(station.stationId, range, pages.cursor);

  const columns = useMemo<DataTableColumn<FuelDailyClosing>[]>(
    () => [
      {
        id: "businessDate",
        header: t("fuel.cost.closings.fields.businessDate"),
        hideable: false,
        exportValue: (row) => row.businessDate,
        cell: (row) => formatDate(row.businessDate),
      },
      {
        id: "product",
        header: t("fuel.cost.fields.product"),
        exportValue: (row) => row.productName,
        cell: (row) => row.productName,
      },
      {
        id: "soldL",
        header: t("fuel.cost.closings.fields.soldL"),
        align: "right",
        exportValue: (row) => row.soldL,
        cell: (row) => <QuantityText value={row.soldL} unit="LITRE" />,
      },
      {
        id: "salesAmount",
        header: t("fuel.cost.closings.fields.salesAmount"),
        align: "right",
        exportValue: (row) => row.salesAmount,
        cell: (row) => Money.fromMinor(row.salesAmount).formatTr(),
      },
      {
        id: "unitCost",
        header: t("fuel.cost.closings.fields.unitCost"),
        align: "right",
        exportValue: (row) => row.unitCost,
        cell: (row) => <UnitCost value={row.unitCost} />,
      },
      {
        id: "cogsAmount",
        header: t("fuel.cost.closings.fields.cogsAmount"),
        align: "right",
        exportValue: (row) => row.cogsAmount,
        cell: (row) => Money.fromMinor(row.cogsAmount).formatTr(),
      },
      {
        id: "cogsLedger",
        header: t("fuel.cost.closings.fields.cogsLedger"),
        exportValue: (row) => FUEL_LEDGER_STATUS_LABELS[row.cogsLedgerStatus],
        cell: (row) => (
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge
              tone={
                row.cogsLedgerStatus === "POSTED"
                  ? "success"
                  : row.cogsLedgerStatus === "QUEUED"
                    ? "warning"
                    : "neutral"
              }
            >
              {FUEL_LEDGER_STATUS_LABELS[row.cogsLedgerStatus]}
            </StatusBadge>
            {row.cogsEntryId ? (
              <Link
                to="/muhasebe/fisler/$id"
                params={{ id: row.cogsEntryId }}
                className="underline underline-offset-4"
              >
                {t("fuel.cost.closings.openEntry")}
              </Link>
            ) : null}
          </span>
        ),
      },
      {
        id: "salesLedger",
        header: t("fuel.cost.closings.fields.salesLedger"),
        defaultVisible: false,
        exportValue: (row) => FUEL_LEDGER_STATUS_LABELS[row.salesLedgerStatus],
        cell: (row) => (
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={row.salesLedgerStatus === "POSTED" ? "success" : "neutral"}>
              {FUEL_LEDGER_STATUS_LABELS[row.salesLedgerStatus]}
            </StatusBadge>
            {row.salesEntryId ? (
              <Link
                to="/muhasebe/fisler/$id"
                params={{ id: row.salesEntryId }}
                className="underline underline-offset-4"
              >
                {t("fuel.cost.closings.openEntry")}
              </Link>
            ) : null}
          </span>
        ),
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <div className="grid gap-4">
      <CloseDay stationId={station.stationId} />
      <DataTable
        caption={t("fuel.cost.closings.caption")}
        columns={columns}
        rows={closings.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={closings.isPending}
        error={closings.error ? <Alert tone="danger">{errorMessage(closings.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<CalendarCheck />}
            title={t("fuel.cost.closings.emptyTitle")}
            description={t("fuel.cost.closings.emptyDescription")}
          />
        }
        filters={<StationField selection={station} />}
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: closings.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(closings.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
    </div>
  );
}

/** "Günü kapat": istasyon × ürün başına SMM fişi. Hassas işlem → step-up; çift tıklamaya karşı idempotent. */
function CloseDay({ stationId }: { stationId: string }): ReactElement | null {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const withStepUp = useStepUp();
  const permissions = useFuelPermissions();
  const [businessDate, setBusinessDate] = useState<string>(() => addDays(toIstanbulDate(new Date()), -1));
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const close = useMutation({
    mutationFn: async () =>
      (
        await withStepUp(() =>
          api.POST("/fuel/daily-closings", {
            params: { header: { "Idempotency-Key": newIdempotencyKey() } },
            body: { stationId, businessDate },
          }),
        )
      ).data!,
    onSuccess: async (result) => {
      setWarnings(result.warnings);
      await refresh();
      toast({ tone: "success", title: t("fuel.cost.closings.closed", { count: result.items.length }) });
    },
    onError: (caught) =>
      setError(
        isApiError(caught, "PERIOD_NOT_CLOSED")
          ? t("fuel.cost.closings.periodNotClosed")
          : errorMessage(caught),
      ),
  });

  if (!permissions.canApproveReconciliation) return null;

  return (
    <Section title={t("fuel.cost.closings.closeTitle")}>
      <p className="text-sm text-muted-foreground">{t("fuel.cost.closings.closeHint")}</p>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {warnings.length > 0 ? (
        <Alert tone="warning" title={t("fuel.cost.closings.warnings")}>
          <ul className="list-inside list-disc">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          setWarnings([]);
          close.mutate();
        }}
      >
        <FormField label={t("fuel.cost.closings.closeDate")} className="w-44" required>
          {(control) => (
            <DateInput
              {...control}
              value={businessDate}
              onValueChange={(value) => value && setBusinessDate(value)}
            />
          )}
        </FormField>
        <Button type="submit" variant="outline" loading={close.isPending}>
          <CalendarCheck aria-hidden="true" />
          {t("fuel.cost.closings.close")}
        </Button>
      </form>
    </Section>
  );
}
